import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Chip,
  Card,
  CardContent,
  Collapse,
  Tooltip,
  Badge,
  alpha
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  ExpandMore,
  ExpandLess,
  SportsSoccer,
  EmojiEvents,
  CalendarMonth,
  ViewWeek,
  Groups,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Agenda.css';
import { usePlayerComparison } from '../context/PlayerComparisonContext';

// Configure dayjs en espagnol
dayjs.locale('es');

// Mapeos pour traduire
const locationMap = {
  maison: 'Local',
  exterieur: 'Visitante'
};

const resultMap = {
  victoire: 'Victoria',
  défaite: 'Derrota'
};

// Couleurs pour les résultats
const resultColors = {
  victoire: { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' },
  défaite: { bg: '#ffebee', text: '#c62828', border: '#ef5350' },
  default: { bg: '#f5f5f5', text: '#616161', border: '#bdbdbd' }
};

/**
 * Composant pour afficher le logo d'une équipe
 */
const TeamLogo = ({ logo, teamName, size = 32 }) => {
  const isUrl = logo && logo.startsWith('http');

  if (isUrl) {
    return (
      <Avatar
        src={logo}
        alt={teamName}
        sx={{
          width: size,
          height: size,
          bgcolor: 'transparent',
          '& img': { objectFit: 'contain' }
        }}
      >
        {teamName?.substring(0, 2).toUpperCase()}
      </Avatar>
    );
  }

  // Fallback: initiales
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: '#1976d2',
        fontSize: size * 0.4,
        fontWeight: 'bold'
      }}
    >
      {teamName?.substring(0, 2).toUpperCase() || '?'}
    </Avatar>
  );
};

/**
 * Composant pour un match dans le dialog
 */
const MatchCard = ({ match, players, mediaUrl, timestamp, searchTerm, compareMode, effectivePlayers, filteredPlayers }) => {
  const [expanded, setExpanded] = useState(false);
  const { teams, round, date, league } = match;
  const teamNames = Object.keys(teams);

  const team1Name = teamNames[0] || 'Equipo 1';
  const team2Name = teamNames[1] || 'Equipo 2';
  const team1Data = teams[team1Name] || {};
  const team2Data = teams[team2Name] || {};

  const resultStyle = team1Data.result ? resultColors[team1Data.result] || resultColors.default : resultColors.default;

  const playersToUse = compareMode ? effectivePlayers : filteredPlayers;

  const getPlayersForTeam = (teamData) => {
    return (teamData.players || [])
      .map(pid => playersToUse.find(p => p.id === pid))
      .filter(Boolean);
  };

  const team1Players = getPlayersForTeam(team1Data);
  const team2Players = getPlayersForTeam(team2Data);
  const totalPlayers = team1Players.length + team2Players.length;

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        border: `1px solid ${resultStyle.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Header du match */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          p: 2,
          bgcolor: alpha(resultStyle.border, 0.05),
          borderBottom: expanded ? `1px solid ${alpha(resultStyle.border, 0.2)}` : 'none'
        }}
      >
        {/* Équipes avec logos */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          {/* Équipe 1 */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <TeamLogo logo={team1Data.logo} teamName={team1Name} size={40} />
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {team1Name}
              </Typography>
              {team1Data.location && (
                <Typography variant="caption" color="text.secondary">
                  {locationMap[team1Data.location] || team1Data.location}
                </Typography>
              )}
            </Box>
          </Box>

          {/* VS */}
          <Box
            sx={{
              px: 2,
              py: 0.5,
              mx: 1,
              borderRadius: 2,
              bgcolor: resultStyle.bg,
              border: `1px solid ${resultStyle.border}`
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              color={resultStyle.text}
            >
              VS
            </Typography>
          </Box>

          {/* Équipe 2 */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
            <Box sx={{ mr: 1.5, textAlign: 'right' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {team2Name}
              </Typography>
              {team2Data.location && (
                <Typography variant="caption" color="text.secondary">
                  {locationMap[team2Data.location] || team2Data.location}
                </Typography>
              )}
            </Box>
            <TeamLogo logo={team2Data.logo} teamName={team2Name} size={40} />
          </Box>
        </Box>

        {/* Info match */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={round}
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
            {team1Data.result && (
              <Chip
                size="small"
                label={resultMap[team1Data.result] || team1Data.result}
                sx={{
                  fontSize: '0.7rem',
                  height: 22,
                  bgcolor: resultStyle.bg,
                  color: resultStyle.text,
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {totalPlayers > 0 && (
              <Chip
                icon={<Groups sx={{ fontSize: 14 }} />}
                size="small"
                label={`${totalPlayers} jugador${totalPlayers > 1 ? 'es' : ''}`}
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </Box>
      </Box>

      {/* Détails des joueurs */}
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 2 }}>
          {/* Joueurs Équipe 1 */}
          {team1Players.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
                {team1Name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {team1Players.map(player => (
                  <Link
                    key={player.id}
                    to={`/players/${player.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <Chip
                      avatar={
                        <Avatar
                          src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                          alt={player.nom}
                        />
                      }
                      label={player.nom}
                      variant="outlined"
                      clickable
                      sx={{
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white'
                        }
                      }}
                    />
                  </Link>
                ))}
              </Box>
            </Box>
          )}

          {/* Joueurs Équipe 2 */}
          {team2Players.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: 'secondary.main' }}>
                {team2Name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {team2Players.map(player => (
                  <Link
                    key={player.id}
                    to={`/players/${player.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <Chip
                      avatar={
                        <Avatar
                          src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                          alt={player.nom}
                        />
                      }
                      label={player.nom}
                      variant="outlined"
                      clickable
                      sx={{
                        '&:hover': {
                          bgcolor: 'secondary.light',
                          color: 'white'
                        }
                      }}
                    />
                  </Link>
                ))}
              </Box>
            </Box>
          )}

          {team1Players.length === 0 && team2Players.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Ningún jugador de tu lista en este partido
            </Typography>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

/**
 * Composant CalendarioResults
 */
const CalendarioResults = ({
  filteredPlayers,
  mediaUrl,
  timestamp,
  isMobile
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const { t } = useTranslation();

  const {
    compareMode,
    effectivePlayers,
    playersToCompare,
    originalPlayers,
    togglePlayerComparison,
    isPlayerSelected,
    enterCompareMode,
    exitCompareMode,
    clearComparison
  } = usePlayerComparison();

  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [schedules, setSchedules] = useState({});

  // Dialog de comparaison
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // ======================================================
  // 1) Charger les données de l'API
  // ======================================================
  useEffect(() => {
    const playersToUse = compareMode ? effectivePlayers : filteredPlayers;

    if (!playersToUse || playersToUse.length === 0) {
      setSchedules({});
      return;
    }
    const playerIds = playersToUse.map((p) => p.id);

    fetch(`${backendUrl}/api/player-stats/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerIds, section })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.schedules) {
          setSchedules(data.schedules);
        } else {
          setSchedules({});
        }
      })
      .catch((err) => {
        console.error('Erreur lors du fetch des horaires:', err);
        setSchedules({});
      });
  }, [filteredPlayers, effectivePlayers, compareMode, backendUrl, section]);

  // ======================================================
  // 2) Construire eventsByDate
  // ======================================================
  const eventsByDate = useMemo(() => {
    const map = {};
    Object.entries(schedules).forEach(([pid, matches]) => {
      matches.forEach((m) => {
        const dateKey = typeof m.game_date === 'string'
          ? m.game_date.split('T')[0]
          : dayjs(m.game_date).format('YYYY-MM-DD');
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push({ playerId: parseInt(pid, 10), match: m });
      });
    });
    return map;
  }, [schedules]);

  // Dialog de comparaison handlers
  const handleOpenCompareDialog = () => {
    setCompareDialogOpen(true);
    setSearchResults(originalPlayers);
  };

  const handleCloseCompareDialog = () => {
    setCompareDialogOpen(false);
    setSearchText('');
    setSearchResults(originalPlayers);
    clearComparison();
  };

  useEffect(() => {
    if (searchText.length < 3) {
      setSearchResults(originalPlayers);
      return;
    }
    const lower = searchText.toLowerCase();
    const matches = originalPlayers.filter((p) =>
      p.nom.toLowerCase().includes(lower)
    );
    setSearchResults(matches);
  }, [searchText, originalPlayers]);

  const handleValidateCompare = () => {
    if (playersToCompare.length < 2) return;
    enterCompareMode();
    setCompareDialogOpen(false);
  };

  // ======================================================
  // 3) Navigation
  // ======================================================
  const currentMonth = currentDate.startOf('month');
  const startOfMonth = currentMonth.startOf('month');
  const endOfMonth = currentMonth.endOf('month');
  const calendarStart = startOfMonth.startOf('week');
  const calendarEnd = endOfMonth.endOf('week');

  const monthCalendarDays = [];
  let tmpDay = calendarStart.clone();
  while (tmpDay.isBefore(calendarEnd, 'day') || tmpDay.isSame(calendarEnd, 'day')) {
    monthCalendarDays.push(tmpDay.clone());
    tmpDay = tmpDay.add(1, 'day');
  }

  const weekStart = currentDate.startOf('week');
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    weekDays.push(weekStart.add(i, 'day'));
  }

  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate((prev) => prev.subtract(1, 'month'));
    } else {
      setCurrentDate((prev) => prev.subtract(1, 'week'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate((prev) => prev.add(1, 'month'));
    } else {
      setCurrentDate((prev) => prev.add(1, 'week'));
    }
  };

  // ======================================================
  // 4) Dialog du jour
  // ======================================================
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayMatches, setSelectedDayMatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLeagues, setExpandedLeagues] = useState([]);

  const handleOpenDayDialog = (dateKey, leagueName = null) => {
    setSelectedDate(dateKey);
    setSelectedDayMatches(eventsByDate[dateKey] || []);
    setSearchTerm('');
    setExpandedLeagues(leagueName ? [leagueName] : []);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDate(null);
    setSelectedDayMatches([]);
    setSearchTerm('');
  };

  // ======================================================
  // 5) Groupement pour le dialog
  // ======================================================
  const dayMatchesGrouped = useMemo(() => {
    if (!selectedDate) return {};
    const grouped = {};
    selectedDayMatches.forEach(({ playerId, match }) => {
      const leagueName = match.league || 'Sin liga';
      if (!grouped[leagueName]) {
        grouped[leagueName] = {};
      }

      const teamA = match.team_name || 'Equipo ?';
      const teamB = match.opponent || 'Oponente ?';
      const sortedTeams = [teamA, teamB].sort();
      const matchKey = `${match.round}_${sortedTeams.join('_vs_')}`;

      if (!grouped[leagueName][matchKey]) {
        grouped[leagueName][matchKey] = {
          date: match.game_date,
          league: leagueName,
          round: match.round,
          teams: {}
        };
      }

      // TeamA
      if (!grouped[leagueName][matchKey].teams[teamA]) {
        grouped[leagueName][matchKey].teams[teamA] = {
          location: match.location,
          result: match.result,
          logo: match.team_logo,
          players: []
        };
      }
      if (!grouped[leagueName][matchKey].teams[teamA].players.includes(playerId)) {
        grouped[leagueName][matchKey].teams[teamA].players.push(playerId);
      }

      // TeamB
      if (!grouped[leagueName][matchKey].teams[teamB]) {
        let secondTeamLocation = null;
        if (match.location === 'maison') secondTeamLocation = 'exterieur';
        if (match.location === 'exterieur') secondTeamLocation = 'maison';

        let secondTeamResult = null;
        if (match.result === 'victoire') secondTeamResult = 'défaite';
        if (match.result === 'défaite') secondTeamResult = 'victoire';

        grouped[leagueName][matchKey].teams[teamB] = {
          location: secondTeamLocation,
          result: secondTeamResult,
          logo: match.opponent_logo,
          players: []
        };
      }
    });
    return grouped;
  }, [selectedDate, selectedDayMatches]);

  // Filtrer
  const filteredDayMatchesGrouped = useMemo(() => {
    if (searchTerm.length < 3) return dayMatchesGrouped;
    const lowerSearch = searchTerm.toLowerCase();
    const newGrouped = {};

    Object.entries(dayMatchesGrouped).forEach(([leagueName, matchesObj]) => {
      const newMatchesObj = {};
      Object.entries(matchesObj).forEach(([matchKey, matchData]) => {
        const { teams } = matchData;
        let matchHasSearch = false;

        const allTeamNames = Object.keys(teams).join(' ').toLowerCase();
        if (allTeamNames.includes(lowerSearch)) {
          matchHasSearch = true;
        } else {
          for (const [, teamData] of Object.entries(teams)) {
            for (const pid of teamData.players) {
              const playersToUse = compareMode ? effectivePlayers : filteredPlayers;
              const pObj = playersToUse.find((p) => p.id === pid);
              if (pObj && pObj.nom.toLowerCase().includes(lowerSearch)) {
                matchHasSearch = true;
                break;
              }
            }
            if (matchHasSearch) break;
          }
        }

        if (matchHasSearch) {
          newMatchesObj[matchKey] = matchData;
        }
      });
      if (Object.keys(newMatchesObj).length > 0) {
        newGrouped[leagueName] = newMatchesObj;
      }
    });
    return newGrouped;
  }, [searchTerm, dayMatchesGrouped, filteredPlayers, compareMode, effectivePlayers]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setExpandedLeagues(Object.keys(filteredDayMatchesGrouped));
    }
  }, [searchTerm, filteredDayMatchesGrouped]);

  const handleToggleLeague = (leagueName) => {
    setExpandedLeagues((prev) =>
      prev.includes(leagueName) ? prev.filter((l) => l !== leagueName) : [...prev, leagueName]
    );
  };

  // ======================================================
  // 6) Helpers pour les vues
  // ======================================================
  const shortWeekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const mobileDays = [];
  for (let i = 0; i < 7; i++) {
    mobileDays.push(dayjs().add(i, 'day'));
  }

  const getDayStats = (dateStr) => {
    const dayEvents = eventsByDate[dateStr] || [];
    const uniquePlayers = Array.from(new Set(dayEvents.map((ev) => ev.playerId)));
    const uniqueMatchKeys = new Set(dayEvents.map((ev) => {
      const teams = [ev.match.team_name, ev.match.opponent].sort();
      return `${ev.match.league}_${ev.match.round}_${teams.join('_vs_')}`;
    }));

    // Grouper par ligue
    const leagueStats = {};
    dayEvents.forEach(({ match }) => {
      const league = match.league || 'Sin liga';
      if (!leagueStats[league]) {
        leagueStats[league] = new Set();
      }
      const teams = [match.team_name, match.opponent].sort();
      leagueStats[league].add(`${match.round}_${teams.join('_vs_')}`);
    });

    return {
      totalMatches: uniqueMatchKeys.size,
      uniquePlayers,
      leagueStats: Object.fromEntries(
        Object.entries(leagueStats).map(([k, v]) => [k, v.size])
      )
    };
  };

  // ======================================================
  // RENDER
  // ======================================================
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Boutons de comparaison */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {!compareMode ? (
          <Button
            variant="contained"
            startIcon={<Groups />}
            onClick={handleOpenCompareDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {t('calendarioResults.buttons.compare')}
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={exitCompareMode}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('calendarioResults.buttons.back')}
          </Button>
        )}
      </Box>

      {/* ===========================
          VUE MOBILE
      ============================ */}
      {isMobile ? (
        <Box>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CalendarMonth color="primary" />
            {t('calendarioResults.mobile.next7Days')}
          </Typography>

          {mobileDays.map((dayItem) => {
            const dayStr = dayItem.format('YYYY-MM-DD');
            const isToday = dayItem.isSame(dayjs(), 'day');
            const { totalMatches, uniquePlayers, leagueStats } = getDayStats(dayStr);
            const upTo4Players = uniquePlayers.slice(0, 4);

            return (
              <Card
                key={dayStr}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: isToday ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  boxShadow: isToday ? '0 4px 12px rgba(25, 118, 210, 0.25)' : '0 2px 8px rgba(0,0,0,0.08)',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Date header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        color: isToday ? 'primary.main' : 'text.primary',
                        textTransform: 'capitalize'
                      }}
                    >
                      {dayItem.format('dddd DD/MM')}
                      {isToday && (
                        <Chip
                          label="Hoy"
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Typography>

                    {totalMatches > 0 && (
                      <Badge badgeContent={totalMatches} color="primary">
                        <SportsSoccer color="action" />
                      </Badge>
                    )}
                  </Box>

                  {/* Avatars des joueurs */}
                  {uniquePlayers.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      {upTo4Players.map((playerId) => {
                        const playersToUse = compareMode ? effectivePlayers : filteredPlayers;
                        const player = playersToUse.find((p) => p.id === playerId);
                        if (!player) return null;
                        return (
                          <Tooltip key={player.id} title={player.nom}>
                            <Link
                              to={`/players/${player.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Avatar
                                src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                                alt={player.nom}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  border: '2px solid white',
                                  ml: -0.5,
                                  '&:first-of-type': { ml: 0 }
                                }}
                              />
                            </Link>
                          </Tooltip>
                        );
                      })}
                      {uniquePlayers.length > 4 && (
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: 'grey.300',
                            fontSize: '0.7rem',
                            ml: -0.5
                          }}
                        >
                          +{uniquePlayers.length - 4}
                        </Avatar>
                      )}
                    </Box>
                  )}

                  {/* Ligues */}
                  {Object.keys(leagueStats).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Object.entries(leagueStats).map(([league, count]) => (
                        <Chip
                          key={league}
                          icon={<EmojiEvents sx={{ fontSize: 16 }} />}
                          label={`${league} (${count})`}
                          size="small"
                          onClick={() => handleOpenDayDialog(dayStr, league)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendarioResults.mobile.noMatches')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        // ===========================
        // VUE DESKTOP / TABLETTE
        // ===========================
        <>
          {/* Barre de navigation */}
          <Card sx={{ mb: 3, borderRadius: 3, p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={handlePrev} sx={{ bgcolor: 'action.hover' }}>
                  <ArrowBackIos fontSize="small" />
                </IconButton>

                <Typography variant="h5" fontWeight="bold" sx={{ minWidth: 200, textAlign: 'center', textTransform: 'capitalize' }}>
                  {viewMode === 'month'
                    ? currentDate.format('MMMM YYYY')
                    : t('calendarioResults.views.weekOf', {
                        start: weekStart.format('DD/MM'),
                        end: weekStart.add(6, 'day').format('DD/MM/YYYY')
                      })}
                </Typography>

                <IconButton onClick={handleNext} sx={{ bgcolor: 'action.hover' }}>
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>

              {/* Boutons de vue */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'week' ? 'contained' : 'outlined'}
                  startIcon={<ViewWeek />}
                  onClick={() => setViewMode('week')}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {t('calendarioResults.views.weeklyView')}
                </Button>
                <Button
                  variant={viewMode === 'month' ? 'contained' : 'outlined'}
                  startIcon={<CalendarMonth />}
                  onClick={() => setViewMode('month')}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {t('calendarioResults.views.monthlyView')}
                </Button>
              </Box>
            </Box>
          </Card>

          {/* ===========================
              VUE MOIS
          =========================== */}
          {viewMode === 'month' && (
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {/* Header jours */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  bgcolor: 'primary.main',
                  color: 'white'
                }}
              >
                {shortWeekDays.map((d) => (
                  <Box key={d} sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {d}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Grille */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)'
                }}
              >
                {monthCalendarDays.map((dayItem) => {
                  const dayStr = dayItem.format('YYYY-MM-DD');
                  const isInThisMonth = dayItem.isSame(currentMonth, 'month');
                  const isToday = dayItem.isSame(dayjs(), 'day');
                  const { totalMatches, uniquePlayers } = getDayStats(dayStr);
                  const upTo3Players = uniquePlayers.slice(0, 3);

                  return (
                    <Box
                      key={dayStr}
                      onClick={() => totalMatches > 0 && handleOpenDayDialog(dayStr)}
                      sx={{
                        minHeight: 100,
                        p: 1,
                        bgcolor: isToday ? alpha('#1976d2', 0.1) : isInThisMonth ? 'white' : 'grey.50',
                        opacity: isInThisMonth ? 1 : 0.5,
                        borderRight: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        cursor: totalMatches > 0 ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        '&:hover': totalMatches > 0 ? {
                          bgcolor: alpha('#1976d2', 0.05),
                          transform: 'scale(1.02)'
                        } : {}
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isToday ? 'bold' : 'normal',
                          color: isToday ? 'primary.main' : 'text.primary',
                          mb: 0.5
                        }}
                      >
                        {dayItem.format('D')}
                      </Typography>

                      {uniquePlayers.length > 0 && (
                        <Box sx={{ display: 'flex', mb: 0.5 }}>
                          {upTo3Players.map((playerId) => {
                            const playersToUse = compareMode ? effectivePlayers : filteredPlayers;
                            const player = playersToUse.find((p) => p.id === playerId);
                            if (!player) return null;
                            return (
                              <Avatar
                                key={player.id}
                                src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                                alt={player.nom}
                                sx={{ width: 20, height: 20, ml: -0.3, '&:first-of-type': { ml: 0 } }}
                              />
                            );
                          })}
                          {uniquePlayers.length > 3 && (
                            <Avatar sx={{ width: 20, height: 20, ml: -0.3, fontSize: '0.6rem', bgcolor: 'grey.400' }}>
                              +{uniquePlayers.length - 3}
                            </Avatar>
                          )}
                        </Box>
                      )}

                      {totalMatches > 0 && (
                        <Chip
                          size="small"
                          label={`${totalMatches} ${totalMatches > 1 ? 'partidos' : 'partido'}`}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'primary.main',
                            color: 'white'
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Card>
          )}

          {/* ===========================
              VUE SEMAINE
          =========================== */}
          {viewMode === 'week' && (
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  bgcolor: 'primary.main',
                  color: 'white'
                }}
              >
                {weekDays.map((dayItem, idx) => {
                  const isToday = dayItem.isSame(dayjs(), 'day');
                  return (
                    <Box
                      key={dayItem.format('YYYY-MM-DD')}
                      sx={{
                        p: 1.5,
                        textAlign: 'center',
                        bgcolor: isToday ? 'primary.dark' : 'transparent'
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {shortWeekDays[idx]}
                      </Typography>
                      <Typography variant="body2">
                        {dayItem.format('DD/MM')}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Contenu */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  minHeight: 300
                }}
              >
                {weekDays.map((dayItem, idx) => {
                  const dayStr = dayItem.format('YYYY-MM-DD');
                  const isToday = dayItem.isSame(dayjs(), 'day');
                  const { totalMatches, uniquePlayers, leagueStats } = getDayStats(dayStr);
                  const upTo3Players = uniquePlayers.slice(0, 3);

                  return (
                    <Box
                      key={dayStr}
                      sx={{
                        p: 1.5,
                        borderRight: idx < 6 ? '1px solid #e0e0e0' : 'none',
                        bgcolor: isToday ? alpha('#1976d2', 0.05) : 'white'
                      }}
                    >
                      {/* Avatars */}
                      {uniquePlayers.length > 0 && (
                        <Box sx={{ display: 'flex', mb: 1.5 }}>
                          {upTo3Players.map((playerId) => {
                            const playersToUse = compareMode ? effectivePlayers : filteredPlayers;
                            const player = playersToUse.find((p) => p.id === playerId);
                            if (!player) return null;
                            return (
                              <Tooltip key={player.id} title={player.nom}>
                                <Link
                                  to={`/players/${player.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Avatar
                                    src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                                    alt={player.nom}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      border: '2px solid white',
                                      ml: -0.5,
                                      '&:first-of-type': { ml: 0 }
                                    }}
                                  />
                                </Link>
                              </Tooltip>
                            );
                          })}
                          {uniquePlayers.length > 3 && (
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: 'grey.300',
                                fontSize: '0.7rem',
                                ml: -0.5
                              }}
                            >
                              +{uniquePlayers.length - 3}
                            </Avatar>
                          )}
                        </Box>
                      )}

                      {/* Total matchs */}
                      {totalMatches > 0 && (
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1, color: 'primary.main' }}
                        >
                          {totalMatches} {totalMatches > 1 ? 'partidos' : 'partido'}
                        </Typography>
                      )}

                      {/* Ligues */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {Object.entries(leagueStats).map(([league, count]) => (
                          <Chip
                            key={league}
                            label={`${league} (${count})`}
                            size="small"
                            onClick={() => handleOpenDayDialog(dayStr, league)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              height: 24,
                              justifyContent: 'flex-start',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          )}
        </>
      )}

      {/* ===========================
          DIALOG MATCHS DU JOUR
      =========================== */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <CalendarMonth />
          {selectedDate
            ? t('calendarioResults.matches.matchesOfDay', { date: dayjs(selectedDate).format('DD/MM/YYYY') })
            : t('calendarioResults.matches.matches')}
        </DialogTitle>

        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          {/* Boutons de comparaison */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            {!compareMode ? (
              <Button
                variant="contained"
                startIcon={<Groups />}
                onClick={handleOpenCompareDialog}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('calendarioResults.buttons.compare')}
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={exitCompareMode}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('calendarioResults.buttons.back')}
              </Button>
            )}
          </Box>

          {/* Recherche */}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={t('calendarioResults.dialog.filterPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white'
              }
            }}
          />
        </Box>

        <DialogContent dividers sx={{ p: 2 }}>
          {Object.keys(filteredDayMatchesGrouped).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SportsSoccer sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                {t('calendarioResults.matches.noMatchesDay')}
              </Typography>
            </Box>
          ) : (
            Object.entries(filteredDayMatchesGrouped).map(([leagueName, matchesObj]) => {
              const matchKeys = Object.keys(matchesObj);
              const isExpanded = expandedLeagues.includes(leagueName);

              return (
                <Card key={leagueName} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                  {/* Header de la ligue */}
                  <Box
                    onClick={() => handleToggleLeague(leagueName)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'grey.100',
                      '&:hover': { bgcolor: 'grey.200' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmojiEvents color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        {leagueName}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${matchKeys.length} ${matchKeys.length > 1 ? 'partidos' : 'partido'}`}
                        color="primary"
                        sx={{ height: 22 }}
                      />
                    </Box>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  {/* Matchs de la ligue */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ p: 2 }}>
                      {matchKeys.map((mk) => (
                        <MatchCard
                          key={mk}
                          match={matchesObj[mk]}
                          mediaUrl={mediaUrl}
                          timestamp={timestamp}
                          searchTerm={searchTerm}
                          compareMode={compareMode}
                          effectivePlayers={effectivePlayers}
                          filteredPlayers={filteredPlayers}
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Card>
              );
            })
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('calendarioResults.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===========================
          DIALOG COMPARAISON
      =========================== */}
      <Dialog
        open={compareDialogOpen}
        onClose={handleCloseCompareDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {t('calendarioResults.comparison.title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder={t('calendarioResults.comparison.searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Joueurs sélectionnés */}
          {playersToCompare.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {playersToCompare.length === 1
                  ? `1 ${t('calendarioResults.comparison.selectedSingular')}`
                  : `${playersToCompare.length} ${t('calendarioResults.comparison.selectedPlural')}`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {playersToCompare.map(player => (
                  <Chip
                    key={player.id}
                    avatar={
                      <Avatar
                        src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                        alt={player.nom}
                      />
                    }
                    label={player.nom || player.name || `Jugador ${player.id}`}
                    onDelete={() => togglePlayerComparison(player)}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}

          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {searchResults.map((player) => {
              const checked = isPlayerSelected(player.id);

              return (
                <ListItem
                  key={player.id}
                  button
                  onClick={() => togglePlayerComparison(player)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: checked ? alpha('#1976d2', 0.1) : 'transparent'
                  }}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={checked}
                      sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                    />
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                      alt={player.nom}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={player.nom} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseCompareDialog}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('calendarioResults.buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleValidateCompare}
            disabled={playersToCompare.length < 2}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('calendarioResults.buttons.validate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarioResults;
